import { useSignal } from '@preact/signals';
import { useEffect, useRef } from 'preact/hooks';

import type { AgendaDay, AgendaEvent, RideItem } from '@amumin/api-client';
import type { ExtrasGet } from '@amumin/domain';
import {
  addableWidgets,
  addHomeCard,
  homeOrderOf,
  keepHiddenWidgets,
  resolveHomeGrid,
  resolveLayout,
  sameHomeCards,
  type HomeCard,
  type HomeCardSize,
  type HomeLayout,
  type WidgetContext,
  type WidgetId,
} from '@amumin/domain/widgets';
import { t } from '@amumin/i18n';
import { Button, showToast } from '@amumin/ui';
import { Avatar, cx, DayHighlights, Icon, ProgressBar } from '@amumin/ui-core';

import { EmptyState } from '../lib/EmptyState.js';
import { api } from '../api.js';
import { PrayerQuestionCard } from '../extras/PrayerQuestion.js';
import { TodayGrid, type TodayGridMode } from '../home/TodayGrid.js';
import {
  MessagesWidget,
  PrayerWidget,
  prayerTodayOf,
  RotationWidget,
  ShoppingWidget,
  WishesWidget,
  type PrayerToday,
} from '../home/HomeWidgets.js';
import { BalanceWidget, BookingsWidget, DinnerWidget } from '../samen/widgets.js';
import { useWeatherLine, WeatherCard } from '../weather/WeatherCard.js';
import { LazyCareAttention, LazyCheckInWidget } from '../care/lazy.js';
import { LazyMoneyWidget } from '../money/lazy.js';
import { LazySchoolTodayCard } from '../school/plan/lazy.js';
import { LazyHuisrondeWidget } from '../huisronde/lazy.js';
import { TodosWidget } from '../home/TodosWidget.js';
import { careOffered, moduleOn, modules, ridesInAgenda } from '../state/modules.js';
import { loadUiPrefs, saveUiPrefs, uiPrefs, uiPrefsReady, UiPrefsNotLoaded } from '../state/ui.js';
import { sharedHouse, tone } from '../state/householdType.js';
import { useEventDialogs } from '../components/EventDialogs.js';
import { EventItem } from '../components/EventItem.js';
import {
  driverOf,
  formatDuration,
  greetingKey,
  hhmmOf,
  joinNames,
  membersOf,
  minutesOf,
  nextUp,
  splitDriversText,
  visibleEvents,
} from '../lib/agenda.js';
import {
  addDays,
  formatDateKeyLong,
  HOUSEHOLD_TZ,
  isoWeekOf,
  minutesNowIn,
  partsIn,
  quickStart,
  todayKey,
} from '../lib/dates.js';
import { openRideLabel } from '../lib/rides.js';
import { progressByMember, tasksForDay } from '../lib/tasks.js';
import { useLive } from '../lib/live.js';
import { ProfileCards } from '../profile/ProfileCards.js';
import { LoadError, mutate, PageHeader, Skeleton, toastError, useLoad } from '../lib/ui.js';
import { householdPaused } from '../lib/paused.js';
import todayStyles from './Today.module.css';
import {
  appText,
  approvalsCount,
  bumpData,
  context,
  household,
  loadHousehold,
  messages,
  ownMemberId,
} from '../state/session.js';

interface TodayData {
  today: AgendaDay;
  tomorrow: AgendaDay | null;
  rides: RideItem[];
}

const EMPTY_DAY = (date: string): AgendaDay => ({ date, weekday_short: '', events: [], tasks: [] });

/**
 * W1 Vandaag (DESIGN.md §8.W1), golf 6b als widgets: iedereen kiest zelf wat er staat en in welke
 * volgorde (Aanpassen), per persoon bewaard op de server. Zonder keuze de standaard, gelijk aan de
 * oude vaste pagina: één focuspunt ("Straks"), open ritten, het weer, de agenda van vandaag, taken
 * per lid, sparen en een blik op morgen. Wat er alleen soms is (dagmarkering, de vraag over
 * gebedstijden, wat wacht op goedkeuring, het gezinsprofiel) blijft buiten de widgets.
 *
 * Golf 8 (0050): een meekijker (rol relative) krijgt een eigen, rustige Vandaag met alleen de
 * check-in (hij mag de agenda van vandaag, taken, ritten en punten niet lezen). Twee componenten,
 * zodat een wissel van huishouden (Anne: eigen gezin en meekijker bij mama) netjes opnieuw begint.
 */
export function TodayPage() {
  const ctx = context.value;
  const role = ctx?.households.find((x) => x.id === ctx.active_household_id)?.role;
  return role === 'relative' ? <RelativeToday /> : <HouseholdToday />;
}

function RelativeToday() {
  const m = messages.value;
  const a = appText.value;
  const tz = household.value?.timezone ?? HOUSEHOLD_TZ;
  const tick = useSignal(0);
  useLive(() => {
    tick.value += 1;
  });
  const key = todayKey(tz);
  return (
    <div data-name="today-page" data-role="relative">
      <PageHeader
        eyebrow={a.today[greetingKey(partsIn(tz).hour)]}
        title={m.web.todayTitle}
        subtitle={formatDateKeyLong(key, m)}
      />
      <LazyCheckInWidget tick={tick.value} relative />
    </div>
  );
}

function HouseholdToday() {
  const m = messages.value;
  const a = appText.value;
  const h = a.home;
  const tz = household.value?.timezone ?? HOUSEHOLD_TZ;
  const dialogs = useEventDialogs();
  const nowMin = useSignal(minutesNowIn(tz));
  const editing = useSignal(false);
  const announce = useSignal('');
  const tick = useSignal(0);
  // Feat/vandaag-raster: raster (computer, tablet) of één kolom (telefoon); TodayGrid meet dit.
  const gridMode = useSignal<TodayGridMode>('list');
  // Ongedaan maken: de opgeslagen indelingen van vóór elke wijziging in deze Aanpassen-ronde.
  const history = useRef<Array<HomeLayout | null>>([]);
  const undoCount = useSignal(0);

  useEffect(() => {
    const timer = setInterval(() => (nowMin.value = minutesNowIn(tz)), 30_000);
    return () => clearInterval(timer);
  }, [tz]);

  useEffect(() => {
    void loadUiPrefs();
  }, []);

  const ctx = context.value;
  const role = ctx?.households.find((x) => x.id === ctx.active_household_id)?.role ?? 'parent';
  const parent = role === 'owner' || role === 'parent';

  // fix/taken-per-dag: blijft de pagina na middernacht open, dan opnieuw laden (de nu-tik hierboven
  // rendert elke 30 s), zodat de kaart nooit de taken van gisteren als vandaag toont.
  const dayKey = todayKey(tz);
  const load = useLoad<TodayData>(async () => {
    const hh = await loadHousehold();
    const key = todayKey(hh.timezone || HOUSEHOLD_TZ);
    const [agenda, rides] = await Promise.all([
      api.app.agenda.get(key, addDays(key, 1)),
      // 0029: zonder Brengen en halen geen "wie rijdt?"-lijst (de route bestaat dan niet).
      moduleOn('rides')
        ? api.app.agenda.rides(48).catch(() => ({ items: [] as RideItem[] }))
        : Promise.resolve({ items: [] as RideItem[] }),
    ]);
    const today =
      agenda.days.find((d) => d.date === agenda.today_key) ?? agenda.days[0] ?? EMPTY_DAY(key);
    const tomorrow = agenda.days.find((d) => d.date === addDays(today.date, 1)) ?? null;
    return { today, tomorrow, rides: rides.items };
  }, [dayKey]);

  // Gebedstijden: alleen voor ouders en alleen met de module (de API geeft een kind niets). Bepaalt
  // ook of het widget te kiezen is: pas als het gezin de tijden koos en ze aan staan.
  const prayerOn = parent && moduleOn('prayer');
  const prayerLoad = useLoad<PrayerToday | null>(
    async () =>
      prayerOn ? prayerTodayOf(await api.app.extras.get<ExtrasGet>().catch(() => null)) : null,
    [prayerOn, tick.value],
  );
  const prayer = prayerLoad.data.value ?? null;
  // Zolang de gebedstijden nog laden, weten we niet of het widget erbij hoort: dan nog niet bewerken.
  // Eén keer geladen is genoeg: een live-poke laadt opnieuw, maar dan blijft de vorige stand staan.
  const prayerSettled = useRef(false);
  if (!prayerLoad.loading.value) prayerSettled.current = true;
  const prayerKnown = !prayerOn || prayerLoad.data.value !== null || prayerSettled.current;

  // Live (SSE-poke bij elke wijziging in het huishouden): neemt de ander een rit over, dan zie je
  // dat hier meteen, ook in een open afspraakblad, in plaats van pas na een 409 (review c-fs r2).
  // Ook je andere apparaten: een nieuwe indeling daar staat hier meteen goed.
  useLive(() => {
    void load.reload();
    dialogs.refresh();
    tick.value += 1;
    if (!editing.value) void loadUiPrefs(true);
  });

  const members = household.value?.members ?? [];
  const data = load.data.value;
  const today = data?.today ?? null;
  const key = today?.date ?? todayKey(tz);
  const hour = partsIn(tz).hour;
  const events = today ? visibleEvents(today, key, new Set()) : [];
  const upcoming = nextUp(events, nowMin.value);
  const progress = progressByMember(today ? tasksForDay([today], today.date) : []);
  const withTasks = members.filter((x) => (progress.get(x.id)?.total ?? 0) > 0);
  const openRides = data?.rides ?? [];
  const openRideText = (r: RideItem) =>
    openRideLabel(
      r,
      r.member_ids === 'everyone'
        ? m.design.eventCard.everyone.toLowerCase()
        : joinNames(
            r.member_ids
              .map((id) => members.find((x) => x.id === id)?.display_name ?? '')
              .filter((n) => n !== ''),
            m.kiosk.and,
          ),
      a.omgeving.rideOpen,
    );
  const quick = quickStart(tz);
  // "Ik rijd" in één tik voor de eerstvolgende open rit (DESIGN W1), met Ongedaan maken.
  const me = ownMemberId.value;
  const firstRide = openRides[0] ?? null;
  const firstRideEv = firstRide
    ? [...(today?.events ?? []), ...(data?.tomorrow?.events ?? [])].find(
        (e) => e.occurrence_id === firstRide.occurrence_id,
      )
    : undefined;
  const iDrive = async () => {
    if (!firstRide || !firstRideEv || !me) return;
    const name = members.find((x) => x.id === me)?.display_name ?? '';
    const res = await mutate(
      () =>
        api.app.occurrences.setDriver(firstRide.occurrence_id, {
          role: firstRide.role,
          member_id: me,
          scope: 'this',
          if_version: firstRideEv.override_version ?? 0,
        }),
      {
        success: t(a.event.driverSaved, { name }),
        undo: async () => {
          if (res) {
            await api.app.occurrences.setDriver(firstRide.occurrence_id, {
              role: firstRide.role,
              member_id: null,
              scope: 'this',
              if_version: res.override_version,
            });
            bumpData();
          }
        },
      },
    );
    if (res) bumpData();
  };

  const newEvent = () => dialogs.openEditor({ kind: 'new', date: quick.date, start: quick.hhmm });
  // 0035 (review g4 ux r2): gepauzeerd = alleen kijken; geen knoppen die toch niets bewaren.
  const paused = householdPaused();
  const open = (ev: AgendaEvent) => dialogs.openSheet(ev, events);

  // ---- Indeling (widgets) ---------------------------------------------------------------------
  // Opnieuw rekenen als de modules veranderen (een module uit = zijn widget weg, zonder te wissen).
  void modules.value;
  const prefs = uiPrefs.value;
  const widgetCtx: WidgetContext = {
    role: role === 'child' ? 'child' : role === 'owner' ? 'owner' : 'parent',
    moduleOn: (id) => moduleOn(id),
    // Zolang de tijden laden, houdt het widget zijn plek (skelet): anders springt de pagina als ze
    // binnenkomen (review golf 6b founder-ux r1).
    // Quick wins 6 okt 2026: geen doelen (sterren) op Vandaag; die staan onder Taken > Sterren.
    extra: { prayer: prayer !== null || !prayerKnown, goals: false },
  };
  // Module Beginscherm op maat uit: iedereen de standaard (de eigen indeling blijft bewaard).
  const custom = prefs ? prefs.home_editable : moduleOn('widgets');
  // Fixer golf 6b r2 (founder-ux): zolang de eigen keuzes nog laden, NIET de standaard tekenen (dat
  // leek op "mijn indeling is weg" en kaarten sprongen onder de duim), maar een skelet.
  const waiting = custom && !prefs && !uiPrefsReady.value;
  const layout = resolveLayout(custom ? (prefs?.home ?? null) : null, widgetCtx);
  // De telefoonvolgorde en het raster (12 kolommen) van de kaarten die nu op Vandaag staan.
  // Afronding "één geheel": een eenmalige vraag of één regel staat nooit in een smalle kolom van het
  // raster. Is het weer de vraag "Wil je weeradvies op het bord?" of even niet bekend, dan staat die
  // regel over de volle breedte bij de meldingen en schuiven de kaarten op (bij Aanpassen blijft de
  // kaart staan).
  const weatherLine = useWeatherLine([...layout.main, ...layout.side].includes('weather'));
  const lift = weatherLine && !editing.value;
  const main = lift ? layout.main.filter((id) => id !== 'weather') : layout.main;
  const side = lift ? layout.side.filter((id) => id !== 'weather') : layout.side;
  const order = [...main, ...side];
  const gridCards = resolveHomeGrid(main, side, layout.cards);
  // 0035: gepauzeerd = alleen kijken; Aanpassen zou toch niets bewaren (423). Fixer golf 6b r2
  // (blocker): pas aanpassen als de eigen keuzes er zijn; anders bewaart één tik "standaard + die
  // tik" over de eigen indeling.
  const editable = custom && prefs !== null && prayerKnown && !paused;
  const addable = addableWidgets(layout, widgetCtx);
  // Fixer golf 6b r1: bij volwassen soorten "Te doen vandaag" in plaats van "Taken vandaag".
  const tn = tone.value;
  const nameOf = (id: WidgetId) => (id === 'tasks' && tn ? tn.tasksWidget : h.widgets[id].name);

  // Module uitgezet terwijl iemand aanpast: dan stopt Aanpassen.
  useEffect(() => {
    if (!editable && editing.value) editing.value = false;
  }, [editable]);

  const say = (text: string) => {
    // Eerst leeg, zodat een schermlezer ook twee keer dezelfde zin voorleest.
    announce.value = '';
    window.setTimeout(() => (announce.value = text), 30);
  };

  const commit = async (next: HomeLayout | null, remember = true) => {
    // Ongedaan maken: eerst de opgeslagen stand van nu onthouden (null = de standaard).
    if (remember) {
      history.current.push(uiPrefs.peek()?.home ?? null);
      undoCount.value = history.current.length;
    }
    try {
      // Fixer golf 6b r2: widgets van een module die nu even uit staat, blijven bewaard op hun plek.
      const stored = uiPrefs.peek()?.home ?? null;
      await saveUiPrefs({
        home: next === null ? null : keepHiddenWidgets(stored, next, widgetCtx),
      });
    } catch (e) {
      if (!(e instanceof UiPrefsNotLoaded)) toastError(e);
      void loadUiPrefs(true);
    }
  };

  // De nieuwste indeling (optimistisch), ook als er sinds deze render al een wijziging bij kwam:
  // elke wijziging rekent daarmee, nooit met een oudere stand (review golf 6b founder-ux r1).
  const latest = (): { order: WidgetId[]; cards: HomeCard[] } => {
    const p = uiPrefs.peek();
    const own = p ? p.home_editable : moduleOn('widgets');
    const l = resolveLayout(own ? (p?.home ?? null) : null, widgetCtx);
    return { order: [...l.main, ...l.side], cards: resolveHomeGrid(l.main, l.side, l.cards) };
  };

  // Raster (computer en tablet): na verplaatsen of een andere maat volgt de telefoon de leesvolgorde
  // van het raster. TodayGrid zegt zelf waar de kaart nu staat (schermlezer).
  const onCards = (next: HomeCard[]) => {
    const base = latest();
    if (sameHomeCards(base.cards, next)) return;
    void commit({ main: homeOrderOf(next), side: [], cards: next });
  };

  // Telefoon: alleen de volgorde; het raster blijft zoals het is.
  const onOrder = (next: WidgetId[], id: WidgetId) => {
    const base = latest();
    if (next.join(',') === base.order.join(',')) return;
    say(t(h.position, { name: nameOf(id), pos: next.indexOf(id) + 1, count: next.length }));
    void commit({ main: next, side: [], cards: base.cards });
  };

  const onRemove = (id: WidgetId) => {
    const base = latest();
    say(t(h.removed, { name: nameOf(id) }));
    // De knop zelf bestaat zo niet meer: de focus naar de kaart erna (of ervoor).
    const at = base.order.indexOf(id);
    const after = base.order[at + 1] ?? base.order[at - 1];
    if (after) {
      window.setTimeout(() => {
        document
          .querySelector<HTMLElement>(
            `[data-widget="${after}"] [data-action="remove"], [data-widget="${after}"] [data-action="move"]`,
          )
          ?.focus();
      }, 60);
    }
    void commit({
      main: base.order.filter((x) => x !== id),
      side: [],
      cards: base.cards.filter((c) => c.kind !== id),
    });
  };

  const add = (id: WidgetId) => {
    say(t(h.added, { name: nameOf(id) }));
    const base = latest();
    void commit({ main: [...base.order, id], side: [], cards: addHomeCard(base.cards, id) });
  };

  const undo = async () => {
    if (history.current.length === 0) return;
    const prev = history.current.pop() ?? null;
    undoCount.value = history.current.length;
    await commit(prev, false);
    say(h.undone);
  };

  const reset = async () => {
    await commit(null);
    showToast({ kind: 'success', message: h.resetDone });
  };

  // Ctrl+Z (Cmd+Z) tijdens Aanpassen: ongedaan maken (niet in een tekstveld).
  useEffect(() => {
    if (!editing.value) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.shiftKey || e.key.toLowerCase() !== 'z') return;
      const el = e.target as HTMLElement | null;
      if (el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName))) return;
      e.preventDefault();
      void undo();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editing.value]);

  const hhmmNow = hhmmOf(nowMin.value);

  const render = (id: WidgetId, _size: HomeCardSize) => {
    switch (id) {
      case 'now':
        return !data ? (
          <Skeleton rows={2} />
        ) : upcoming ? (
          <FocusCard
            ev={upcoming.event}
            status={upcoming.status}
            minutes={upcoming.minutes}
            onOpen={open}
          />
        ) : events.length === 0 ? null : (
          // Een lege dag: alleen de lege staat in de agendakaart (met "+ Afspraak"), niet twee keer
          // dezelfde illustratie onder elkaar (polish w4 ronde 2).
          // UX-ronde 2: een kleine regel, geen illustratie (de dag is voorbij; de agenda staat eronder).
          <div class="status-line status-line-quiet" data-name="focus-empty">
            <span class="status-line-icon" aria-hidden="true">
              <Icon id="circle-check" size={20} />
            </span>
            <p class="status-line-text">
              <strong>{a.today.nothingNext}</strong>
              <span class="status-line-sub">{a.today.nothingNextBody}</span>
            </p>
          </div>
        );
      case 'rides':
        return openRides.length > 0 ? (
          // UX-ronde 2: een waarschuwing als één regel (smalle streep, geen gekleurd vlak).
          <div class="status-line status-line-ask" data-name="rides-alert">
            <span class="status-line-icon" aria-hidden="true">
              <Icon id="car" size={20} />
            </span>
            {/* De regel zelf gaat naar alle ritten; de enige knop is "Ik rijd". */}
            <a
              class="status-line-text status-line-link"
              href="/ritten"
              data-name="rides-open"
              data-fit-line=""
            >
              <strong>{t(a.today.ridesOpen, { count: openRides.length })}</strong>
              {openRides[0] ? (
                <span class="status-line-sub" data-fit-drop="">
                  {openRides[0].at_hhmm} · {openRideText(openRides[0])}
                </span>
              ) : null}
            </a>
            {firstRideEv && me && !paused ? (
              <span class="status-line-actions">
                <Button name="rides-i-drive" size="small" onPress={() => void iDrive()}>
                  {a.event.iDrive}
                </Button>
              </span>
            ) : null}
          </div>
        ) : null;
      // 0026: weer vandaag (of één keer de uitnodiging om een locatie te kiezen); tik = detail.
      case 'weather':
        return <WeatherCard members={members} />;
      case 'prayer':
        return !prayerKnown ? (
          <Skeleton rows={3} />
        ) : (
          <PrayerWidget
            prayer={prayer}
            nowHhmm={hhmmNow}
            onSeen={() => {
              tick.value += 1;
            }}
          />
        );
      case 'agenda':
        return (
          <section class="panel" aria-labelledby="today-agenda-title" data-name="today-events">
            <div class="section-head">
              <h2 class="section-title" id="today-agenda-title">
                {a.today.agenda}
              </h2>
              <a href="/agenda" class="small">
                {a.today.toAgenda}
              </a>
            </div>
            {!data ? (
              <Skeleton rows={3} />
            ) : events.length === 0 ? (
              <EmptyState
                size="compact"
                title={m.design.empty.emptyDayTitle}
                action={
                  paused ? undefined : (
                    <Button name="empty-new-event" icon="plus" onPress={newEvent}>
                      {a.today.addEventLong}
                    </Button>
                  )
                }
              />
            ) : (
              <ul class="event-list">
                {events.map((ev) => {
                  const past = !ev.all_day && minutesOf(ev.end_hhmm) <= nowMin.value;
                  return (
                    // Past de kaart niet alles, dan gaan afspraken die voorbij zijn het eerst weg.
                    <li key={ev.occurrence_id} data-fit-early={past ? '' : undefined}>
                      <EventItem
                        ev={ev}
                        members={members}
                        onOpen={open}
                        tone={
                          upcoming?.event.occurrence_id === ev.occurrence_id
                            ? 'next'
                            : past
                              ? 'past'
                              : 'default'
                        }
                      />
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        );
      case 'tasks':
        // Huisgenoten: het rooster staat bovenaan; een lege takenkaart zegt dan niets (review g6b ux).
        if (data && withTasks.length === 0 && sharedHouse.value) return null;
        return (
          <section class="panel" aria-labelledby="today-tasks-title" data-name="today-tasks">
            <div class="section-head">
              <h2 class="section-title" id="today-tasks-title">
                {tn ? tn.tasksWidget : a.today.tasks}
              </h2>
              <a href="/taken" class="small">
                {tn ? tn.tasksAll : a.today.tasksAll}
              </a>
            </div>
            {!data ? (
              <Skeleton rows={3} />
            ) : withTasks.length === 0 ? (
              <EmptyState
                size="compact"
                title={tn ? tn.noTasksTitle : m.design.empty.noTasksTitle}
                body={tn ? tn.noTasksBody : undefined}
              />
            ) : (
              <ul class="member-progress">
                {withTasks.map((member) => {
                  const p = progress.get(member.id) ?? { done: 0, total: 0 };
                  return (
                    <li key={member.id}>
                      <Avatar
                        name={member.display_name}
                        colorKey={member.color_key}
                        icon={member.avatar_icon}
                        size="small"
                      />
                      <div class="member-progress-body">
                        <div class="member-progress-name">
                          <span>{member.display_name}</span>
                          <span>{t(m.design.progress, { done: p.done, total: p.total })}</span>
                        </div>
                        <ProgressBar
                          value={p.done}
                          max={p.total}
                          size="sm"
                          label={t(m.design.progress, { done: p.done, total: p.total })}
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        );
      // Quick wins 6 okt 2026: sterren en cadeautjes alleen onder Taken > Sterren, niet op Vandaag
      // (zie ook widgetCtx.extra.goals). Een oude indeling met dit widget toont het niet meer.
      case 'goals':
        return null;
      case 'tomorrow':
        return (
          <section class="panel" aria-labelledby="today-tomorrow-title" data-name="today-tomorrow">
            {/* Fijnslijpen (7 okt 2026): dezelfde kop als de andere kaarten (was een klein label in
                hoofdletters). */}
            <div class="section-head">
              <h2 class="section-title" id="today-tomorrow-title">
                {a.today.tomorrow}
              </h2>
            </div>
            {!data ? null : !data.tomorrow ||
              visibleEvents(data.tomorrow, key, new Set()).length === 0 ? (
              <p class="muted small">{a.today.tomorrowNothing}</p>
            ) : (
              // Hooguit zes; in het raster laat de kaart zien wat past en staat de rest achter
              // "+n meer" (geen scroll in kaarten, 6 okt; was: 2, 3 of 6 naar de maat).
              <ul
                class="tomorrow-list"
                data-fit-extra={Math.max(
                  0,
                  visibleEvents(data.tomorrow, key, new Set()).length - 6,
                )}
              >
                {visibleEvents(data.tomorrow, key, new Set())
                  .slice(0, 6)
                  .map((ev) => (
                    <li key={ev.occurrence_id}>
                      <span class="tomorrow-time">{ev.all_day ? '—' : ev.start_hhmm}</span>
                      <span class="tomorrow-text">
                        <span>{ev.title}</span>
                        <TomorrowRide ev={ev} />
                      </span>
                    </li>
                  ))}
              </ul>
            )}
          </section>
        );
      case 'rotation':
        return <RotationWidget tick={tick.value} />;
      // Golf 8 (0049): Samen wonen; een slapende module rendert niets.
      case 'dinner':
        return <DinnerWidget tick={tick.value} />;
      case 'bookings':
        return <BookingsWidget tick={tick.value} />;
      case 'balance':
        return <BalanceWidget tick={tick.value} />;
      case 'shopping':
        return <ShoppingWidget tick={tick.value} />;
      case 'messages':
        return <MessagesWidget tick={tick.value} />;
      case 'wishes':
        return <WishesWidget tick={tick.value} />;
      // Golf 8 (0050): dagelijkse check-in (eigen chunk).
      case 'check_in':
        return <LazyCheckInWidget tick={tick.value} />;
      // 0069: Geldzaken (alleen in je eigen omgeving; eigen chunk).
      case 'money':
        return <LazyMoneyWidget tick={tick.value} />;
      // Oprichter 6 okt: jouw to-do's (in Mijn omgeving ook wat het gezin aan jou toewees).
      case 'todos':
        return <TodosWidget tick={tick.value} />;
      // 0076: huiswerk, toetsen en beoordelingen om in te voeren (module School).
      case 'school':
        return <LazySchoolTodayCard tick={tick.value} />;
      // 0075: Huisronde (module Eten; eigen chunk).
      case 'house_round':
        return <LazyHuisrondeWidget tick={tick.value} />;
      default:
        return null;
    }
  };

  const empty = !waiting && order.length === 0;
  const startEditing = () => {
    history.current = [];
    undoCount.value = 0;
    editing.value = true;
  };

  return (
    <div data-name="today-page" data-editing={editing.value ? 'true' : undefined}>
      <PageHeader
        title={m.web.todayTitle}
        // WP4: groet, datum en week op één regel (was twee regels boven de eerste kaart).
        subtitle={
          <>
            {a.today[greetingKey(hour)]} · {formatDateKeyLong(key, m)} ·{' '}
            {t(a.today.week, { week: isoWeekOf(key) })}
          </>
        }
        actions={
          editing.value ? (
            <Button
              name="home-done"
              variant="primary"
              icon="check"
              onPress={() => {
                editing.value = false;
                history.current = [];
                undoCount.value = 0;
              }}
            >
              {h.done}
            </Button>
          ) : (
            <>
              {editable ? (
                <span class="home-customize">
                  <Button
                    name="home-customize"
                    variant="ghost"
                    icon="sliders-horizontal"
                    onPress={startEditing}
                  >
                    {h.customize}
                  </Button>
                </span>
              ) : null}
              {paused ? null : (
                <span class="desktop-only">
                  <Button name="new-event" variant="primary" icon="plus" onPress={newEvent}>
                    {a.today.addEvent}
                  </Button>
                </span>
              )}
            </>
          )
        }
      />

      {editing.value ? (
        <div class="home-edit-bar" data-name="home-edit-bar">
          <p class="home-edit-intro">{gridMode.value === 'list' ? h.introList : h.intro}</p>
          <span class="home-edit-actions">
            <Button
              name="home-undo"
              variant="ghost"
              size="small"
              icon="undo-2"
              disabled={undoCount.value === 0}
              onPress={() => void undo()}
            >
              {h.undo}
            </Button>
            <Button
              name="home-reset"
              variant="ghost"
              size="small"
              icon="rotate-ccw"
              onPress={() => void reset()}
            >
              {h.reset}
            </Button>
          </span>
        </div>
      ) : (
        <div class="home-notices">
          {/* Golf 8 review r2: bovenaan wat de check-in van jou vraagt (toestemming, goedkeuren,
              "staat nog uit"); tekent niets als er niets is. */}
          {careOffered() && role !== 'child' && !paused ? (
            <LazyCareAttention tick={tick.value} />
          ) : null}
          {/* w4 familie: verjaardag, feestdag of schoolvakantie van vandaag (DESIGN.md §8.F). */}
          {today?.highlights && today.highlights.length > 0 ? (
            <DayHighlights items={today.highlights} />
          ) : null}
          {/* 0027: één keer de vraag "Gebedstijden op het bord?" (alleen bij aanbod, tot het gezin
              koos). 0035: gepauzeerd = alleen kijken; geen instelvragen die toch niets bewaren. */}
          {moduleOn('prayer') && !paused ? <PrayerQuestionCard name="today" banner /> : null}
          {lift ? <WeatherCard members={members} /> : null}
          {approvalsCount.value > 0 ? (
            <a class="inbox-card" href="/goedkeuren" data-name="today-approvals">
              <span class="inbox-card-icon">
                <Icon id="inbox" size={22} />
              </span>
              <span class="inbox-card-text">
                {t(a.today.approvalsWaiting, { count: approvalsCount.value })}
              </span>
              <Icon id="chevron-right" size={20} />
            </a>
          ) : null}
        </div>
      )}

      {load.error.value && !data ? (
        <LoadError message={load.error.value} onRetry={() => void load.reload()} />
      ) : null}

      {empty && !editing.value ? (
        <EmptyState
          title={h.empty}
          body={h.emptyBody}
          {...(editable
            ? {
                action: (
                  <Button
                    name="home-empty-customize"
                    icon="sliders-horizontal"
                    onPress={startEditing}
                  >
                    {h.customize}
                  </Button>
                ),
              }
            : {})}
        />
      ) : waiting ? (
        <HomeSkeleton />
      ) : (
        <TodayGrid
          order={order}
          cards={gridCards}
          editing={editing.value}
          name={nameOf}
          render={render}
          onCards={onCards}
          onOrder={onOrder}
          onRemove={onRemove}
          say={say}
          onMode={(mode) => (gridMode.value = mode)}
        />
      )}

      {editing.value ? (
        <section class="panel home-add" aria-labelledby="home-add-title" data-name="home-add">
          <h2 class="section-title" id="home-add-title">
            {h.addTitle}
          </h2>
          {empty ? <p class="muted small">{h.emptyEdit}</p> : null}
          {addable.length === 0 ? (
            <p class="muted small">{h.addNothing}</p>
          ) : (
            <ul class="home-add-list">
              {addable.map((id) => (
                <li key={id} data-widget-add={id}>
                  <span class="home-add-text">
                    <strong>{h.widgets[id].name}</strong>
                    <span class="muted small block">{h.widgets[id].description}</span>
                  </span>
                  <Button
                    name={`home-add-${id}`}
                    size="small"
                    icon="plus"
                    label={`${h.add}: ${h.widgets[id].name}`}
                    onPress={() => add(id)}
                  />
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : // 0023: uitnodiging "Wat past bij jullie gezin?" en de kaart voor een andere ouder: compact
      // en onder de widgets, zodat vandaag bovenaan blijft (review d-ux r1). Gepauzeerd (0035): weg,
      // "Instellen" zou toch niets bewaren (review g4 af r1).
      paused ? null : (
        <ProfileCards />
      )}

      <p class="fb-sr-only" role="status" aria-live="polite" data-name="home-announce">
        {announce.value}
      </p>

      {paused ? null : (
        <button
          type="button"
          class="fab"
          aria-label={a.today.addEventLong}
          onClick={newEvent}
          data-name="fab-new-event"
        >
          <Icon id="plus" size={28} />
        </button>
      )}

      {dialogs.render(members)}
    </div>
  );
}

/**
 * Ronde 1: wie rijdt morgen, onder de afspraak in "Morgen" (zelfde zin als de lijst en de
 * focuskaart: "Anne rijdt", "Baba brengt · Anne haalt", "Wie rijdt?"). Niets zonder ritten.
 */
/**
 * Vandaag terwijl de eigen keuzes nog laden en er op dit apparaat nog geen indeling bekend is
 * (fixer golf 6b r2): rustige grijze kaarten in de twee kolommen, geen standaardindeling.
 */
function HomeSkeleton() {
  return (
    <div class="today-grid" data-name="home-loading" aria-busy="true">
      <div class="today-col" data-column-loading="main">
        <div class="panel">
          <Skeleton rows={2} />
        </div>
        <div class="panel">
          <Skeleton rows={4} />
        </div>
      </div>
      <div class="today-col" data-column-loading="side">
        <div class="panel">
          <Skeleton rows={3} />
        </div>
      </div>
    </div>
  );
}

function TomorrowRide({ ev }: { ev: AgendaEvent }) {
  const m = messages.value;
  const members = household.value?.members ?? [];
  if (!ridesInAgenda() || ev.cancelled) return null;
  const driver = driverOf(ev, members);
  if (driver === null) return null;
  if (driver === 'missing') {
    return (
      <span class="tomorrow-ride tomorrow-ride-alert" data-name="tomorrow-ride">
        <Icon id="car" size={14} /> {m.design.eventCard.whoDrives}
      </span>
    );
  }
  const split = splitDriversText(ev, members, m.design.eventCard.splitDrives);
  return (
    <span class="tomorrow-ride" data-name="tomorrow-ride">
      <Icon id="car" size={14} />{' '}
      {split ?? m.design.eventCard.drives.replace('{name}', driver.name)}
    </span>
  );
}

/** Het ene focuspunt van het scherm: wat nu bezig is of straks begint. */
function FocusCard({
  ev,
  status,
  minutes,
  onOpen,
}: {
  ev: AgendaEvent;
  status: 'now' | 'next';
  minutes: number;
  onOpen: (ev: AgendaEvent) => void;
}) {
  const a = appText.value;
  const m = messages.value;
  const members = household.value?.members ?? [];
  const who = membersOf(ev.member_ids, members);
  // 0029: zonder "Brengen en halen in de agenda tonen" ook geen ritregel op de focuskaart.
  const rides = ridesInAgenda();
  const driver = rides ? driverOf(ev, members) : null;
  // Zelfde tekst als de lijst eronder (EventItem): "Baba brengt · Anne haalt" (review c-ux r3).
  const split = rides ? splitDriversText(ev, members, m.design.eventCard.splitDrives) : null;
  return (
    <button
      type="button"
      class={cx('focus-card', todayStyles.focus)}
      onClick={() => onOpen(ev)}
      data-name="focus-card"
    >
      {/* Restpunten (docs/ux/restpunten): geen ledenkleur en geen pictogram op de focuskaart; één
          accent (het merkblauw), zoals op het bord. */}
      <span class="focus-body">
        <span class="focus-eyebrow">{status === 'now' ? a.today.now : a.today.next}</span>
        <span class="focus-title block">{ev.title}</span>
        <span class="focus-meta" data-fit-row="">
          <span class="focus-count">
            {status === 'now'
              ? t(a.today.endsIn, { time: formatDuration(minutes, a) })
              : t(a.today.startsIn, { time: formatDuration(minutes, a) })}
          </span>
          <span class="tabular">
            {ev.start_hhmm}–{ev.end_hhmm === '24:00' ? '00:00' : ev.end_hhmm}
          </span>
          {/* Review g3-ux r1 (oprichter #5): het nuttige moment is "Vertrek 16:20", niet alleen
              de begintijd. */}
          {status === 'next' && ev.travel ? (
            <span class="tag" data-name="focus-depart">
              <Icon id="car" size={14} /> {t(a.event.depart, { time: ev.travel.depart_hhmm })}
            </span>
          ) : null}
          {who.length > 0 && who.length < members.length ? (
            <span class="avatar-stack">
              {who.slice(0, 4).map((x) => (
                <Avatar
                  key={x.id}
                  name={x.display_name}
                  colorKey={x.color_key}
                  icon={x.avatar_icon}
                  size="chip"
                />
              ))}
            </span>
          ) : null}
          {driver === 'missing' ? (
            <span class="tag tag-ask">
              <Icon id="car" size={14} /> {m.design.eventCard.whoDrives}
            </span>
          ) : driver ? (
            <span class="tag">
              <Icon id="car" size={14} />{' '}
              {split ?? m.design.eventCard.drives.replace('{name}', driver.name)}
            </span>
          ) : null}
        </span>
      </span>
    </button>
  );
}
